import './App.css';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';

const LAST_VISIT_KEY = 'scene_last_visit';

function hashStringToHue(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
}

function truncateText(text, limit = 280) {
  if (!text) return '';
  if (text.length <= limit) return text;
  return text.slice(0, limit).trimEnd() + '...';
}

function authorColor(author) {
  const name = author || 'Unknown';
  const hue = hashStringToHue(name);
  return `hsl(${hue}, 70%, 50%)`;
}

function authorInitials(author) {
  if (!author) return '?';
  const parts = author.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function formatTimeAgo(date) {
  if (!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

function YoutubeIcon() {
  return (
    <span className="platform-icon" aria-label="YouTube">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="4" ry="4" fill="#ff0000" />
        <polygon points="10,9 16,12 10,15" fill="#ffffff" />
      </svg>
    </span>
  );
}

function XIcon() {
  return (
    <span className="platform-icon" aria-label="X">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect width="24" height="24" rx="4" fill="#000000" />
        <path d="M17.5 3.5h2.5l-5.5 6.3 6.5 8.7h-5l-3.9-5.1-4.5 5.1H5l5.8-6.6L4.5 3.5h5.1l3.5 4.7 4.4-4.7z" fill="#ffffff" />
      </svg>
    </span>
  );
}

function Avatar({ author, active, avatarUrl }) {
  const color = authorColor(author);
  const initials = authorInitials(author);
  if (avatarUrl) {
    return (
      <div
        className={`avatar ${active ? 'avatar-active' : 'avatar-inactive'}`}
        style={{ borderColor: color }}
      >
        <img
          src={avatarUrl}
          alt={author}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
    );
  }
  return (
    <div
      className={`avatar ${active ? 'avatar-active' : 'avatar-inactive'}`}
      style={{ borderColor: color }}
    >
      <div className="avatar-inner" style={{ color }}>
        {initials}
      </div>
    </div>
  );
}

function ConvergenceCard({ highlight, postsById, devMode, creatorAvatars, isExpanded, onToggleExpand }) {
  const posts = (highlight.post_ids || [])
    .map((id) => postsById.get(String(id)))
    .filter(Boolean);

  if (!posts.length) return null;

  const isCooling = highlight.status === 'cooling';
  const displayedPosts = isExpanded ? posts : posts.slice(0, 3);

  const mostRecent = posts.reduce((latest, post) => {
    const ts = post.published_at ? new Date(post.published_at) : null;
    if (!ts) return latest;
    if (!latest) return ts;
    return ts > latest ? ts : latest;
  }, null);

  const authorSet = new Set(posts.map((p) => p.author || 'Unknown'));
  const authorCount = authorSet.size;
  const shownAuthorSet = new Set(displayedPosts.map((p) => p.author || 'Unknown'));
  const extraAuthorCount = Math.max(authorCount - shownAuthorSet.size, 0);

  return (
    <article className={`card card-convergence ${isCooling ? 'card-cooling' : ''}`}>
      <header className="card-top">
        <div className="topic-badge">
          <span className="topic-text">{highlight.topic}</span>
          <span className="topic-meta">{authorCount} voice{authorCount === 1 ? '' : 's'}</span>
        </div>
        {mostRecent && <span className="timestamp">{formatTimeAgo(mostRecent)}</span>}
      </header>

      <div className="card-content">
        {displayedPosts.map((post) => {
            const platform = post.platform === 'twitter' ? 'x' : post.platform;
            const key = `${platform}:${post.author}`;
            const creator = creatorAvatars?.byKey?.get(key);
            const displayName = creator?.displayName || post.author;
            const avatarUrl = creator?.avatarUrl || null;
            return (
          <div key={post.id} className="convergence-row">
            <Avatar
              author={displayName}
              active
              avatarUrl={avatarUrl}
            />
            <div className="convergence-main">
              <div className="convergence-meta">
                <span className="convergence-author">{displayName}</span>
                {post.platform === 'twitter' ? <XIcon /> : <YoutubeIcon />}
              </div>
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="convergence-title"
              >
                {post.post_type === 'retweet' && <span className="rt-prefix">RT </span>}{truncateText(post.content)}
              </a>
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="convergence-link"
              >
                {post.platform === 'twitter' ? 'view on X' : 'watch on YouTube'}
              </a>
            </div>
          </div>
            );
          })}
        {extraAuthorCount > 0 && (
          <button
            className="convergence-more"
            onClick={onToggleExpand}
            style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left' }}
          >
            {isExpanded
              ? `− show ${extraAuthorCount} fewer voice${extraAuthorCount === 1 ? '' : 's'}`
              : `+ ${extraAuthorCount} more voice${extraAuthorCount === 1 ? '' : 's'}`}
          </button>
        )}
        {devMode && (
          <div style={{ marginTop: '8px', padding: '6px 8px', background: '#f0f0f0', borderRadius: '4px', fontSize: '0.7rem', color: '#666', fontFamily: 'monospace' }}>
            🛠 voices: {highlight.voice_count || 1} | status: {highlight.status} | score: {highlight.score?.toFixed(1)} | last active: {highlight.last_active_at ? new Date(highlight.last_active_at).toLocaleString() : 'n/a'}
          </div>
        )}
      </div>
    </article>
  );
}

function BreakoutCard({ highlight, postsById, devMode, creatorAvatars }) {
  const firstPostId = (highlight.post_ids && highlight.post_ids[0]) || null;
  const post = firstPostId ? postsById.get(String(firstPostId)) : null;

  if (!post) return null;

  const published = post.published_at ? new Date(post.published_at) : null;

  const platform = post.platform === 'twitter' ? 'x' : post.platform;
      const key = `${platform}:${post.author}`;
      const creator = creatorAvatars?.byKey?.get(key);
      const displayName = creator?.displayName || post.author;
      const avatarUrl = creator?.avatarUrl || null;

  return (
    <article className="card card-breakout">
      <header className="card-top">
        <div className="card-left">
          <Avatar
            author={displayName}
            active
            avatarUrl={avatarUrl}
          />
          <div>
            <div className="breakout-author-row">
              <span className="breakout-author">{displayName}</span>
              {post.platform === 'twitter' ? <XIcon /> : <YoutubeIcon />}
            </div>
            {published && (
              <span className="timestamp subtle">{formatTimeAgo(published)}</span>
            )}
          </div>
        </div>
        <span className="trending-badge">⚡</span>
      </header>

      <div className="card-content">
        <a
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="breakout-title"
        >
          {post.post_type === 'retweet' && <span className="rt-prefix">RT </span>}{truncateText(post.content)}
        </a>
        <div className="breakout-stats">
          <span className="stat">
            <span className="stat-label">Views</span>
            <span className="stat-value">
              {post.views?.toLocaleString?.() ?? post.views}
            </span>
          </span>
          <span className="stat">
            <span className="stat-label">Likes</span>
            <span className="stat-value">
              {post.likes?.toLocaleString?.() ?? post.likes}
            </span>
          </span>
          <span className="stat">
            <span className="stat-label">Comments</span>
            <span className="stat-value">
              {post.comments?.toLocaleString?.() ?? post.comments}
            </span>
          </span>
        </div>
        <a
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="breakout-link"
        >
          {post.platform === 'twitter' ? 'view on X' : 'watch on YouTube'}
        </a>
      </div>
    </article>
  );
}

function TimelineView({ timelineConvergence, timelineBreakouts, postsById, creatorAvatars, expandedIds, toggleExpanded }) {
  return (
    <div style={{ padding: '24px 16px', background: '#f9f9f9', minHeight: '100vh' }}>

      {/* Convergence Stories */}
      <div style={{ marginBottom: '48px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '4px', color: '#333' }}>
          Convergence Stories — All Time ({timelineConvergence.length})
        </h2>
        <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '16px', display: 'flex', gap: '16px' }}>
          <span>🟢 active (&lt;6hrs)</span>
          <span>🟡 warming (6-24hrs)</span>
          <span>⚫ cooling (24-48hrs)</span>
          <span>⬜ archived (48hrs+)</span>
        </div>
        <div className="grid">
          {timelineConvergence.map((h) => (
            <ConvergenceCard
              key={h.id}
              highlight={h}
              postsById={postsById}
              devMode={true}
              creatorAvatars={creatorAvatars}
              isExpanded={expandedIds.has(h.id)}
              onToggleExpand={() => toggleExpanded(h.id)}
            />
          ))}
        </div>
      </div>

      {/* Breakout Posts by Day */}
      <div>
        <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '16px', color: '#333' }}>
          Breakout Posts — By Day
        </h2>
        {Object.entries(timelineBreakouts).map(([day, items]) => (
          <div key={day} style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#666', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{day}</div>
            <div className="grid">
              {items.map((h) => (
                <BreakoutCard key={h.id} highlight={h} postsById={postsById} devMode={true} creatorAvatars={creatorAvatars} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function App() {
  const [unlocked, setUnlocked] = useState(() => {
    try { return window.localStorage.getItem('scene_unlocked') === 'true'; }
    catch { return false; }
  });
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [posts, setPosts] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSinceLastVisit, setNewSinceLastVisit] = useState(0);
  const [devMode, setDevMode] = useState(() => {
    try { return window.localStorage.getItem('scene_devmode') === 'true'; } catch { return false; }
  });
  const [activeTab, setActiveTab] = useState('feed');
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleUnlock = () => {
    if (pwInput === 'scene2025') {
      try { window.localStorage.setItem('scene_unlocked', 'true'); } catch {}
      setUnlocked(true);
    } else if (pwInput === 'scenedev') {
      try {
        window.localStorage.setItem('scene_unlocked', 'true');
        window.localStorage.setItem('scene_devmode', 'true');
      } catch {}
      setUnlocked(true);
      setDevMode(true);
    } else {
      setPwError(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [highlightsRes, creatorsRes] = await Promise.all([
          supabase
            .from('highlights')
            .select('id, type, topic, post_ids, score, authors, created_at, voice_count, status, last_active_at')
            .order('created_at', { ascending: false })
            .limit(500),
          supabase
            .from('creators')
            .select('account_id, display_name, platform, avatar_url'),
        ]);

        if (highlightsRes.error) {
          throw highlightsRes.error;
        }
        if (creatorsRes.error) {
          throw creatorsRes.error;
        }

        const highlightsData = highlightsRes.data || [];
        setHighlights(highlightsData);
        setCreators(creatorsRes.data || []);

        // Collect all unique post IDs referenced by highlights
        const postIdSet = new Set();
        for (const h of highlightsData) {
          for (const pid of (h.post_ids || [])) {
            postIdSet.add(pid);
          }
        }
        const allPostIds = Array.from(postIdSet);

        // Fetch only the posts referenced by highlights
        let postsData = [];
        if (allPostIds.length > 0) {
          const postsRes = await supabase
            .from('posts')
            .select('id, author, platform, content, url, published_at, views, likes, comments, post_type')
            .in('id', allPostIds);
          if (postsRes.error) {
            throw postsRes.error;
          }
          postsData = postsRes.data || [];
        }
        setPosts(postsData);

        try {
          const now = new Date();
          const lastVisitRaw = window.localStorage.getItem(LAST_VISIT_KEY);
          let lastVisitCount = 0;
          if (lastVisitRaw) {
            const lastVisit = new Date(lastVisitRaw);
            lastVisitCount = highlightsData.filter((h) => {
              if (!h.created_at) return false;
              const created = new Date(h.created_at);
              return created > lastVisit;
            }).length;
          }
          setNewSinceLastVisit(lastVisitCount);
          window.localStorage.setItem(LAST_VISIT_KEY, now.toISOString());
        } catch {
          // Ignore localStorage errors
        }
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const postsById = useMemo(() => {
    const map = new Map();
    for (const post of posts) {
      map.set(String(post.id), post);
    }
    return map;
  }, [posts]);

  const creatorAvatars = useMemo(() => {
    const byKey = new Map();
    for (const c of creators) {
      byKey.set(`${c.platform}:${c.account_id}`, {
        displayName: c.display_name,
        avatarUrl: c.avatar_url
      });
    }
    return { byKey };
  }, [creators]);

  const convergenceHighlights = useMemo(
    () => highlights
      .filter((h) => h.type === 'convergence' && (devMode || h.status !== 'archived'))
      .sort((a, b) => {
        const voiceDiff = (b.voice_count || 1) - (a.voice_count || 1);
        if (voiceDiff !== 0) return voiceDiff;
        return new Date(b.last_active_at || b.created_at) - new Date(a.last_active_at || a.created_at);
      }),
    [highlights, devMode],
  );

  const breakoutHighlights = useMemo(
    () => highlights.filter((h) => h.type === 'breakout'),
    [highlights],
  );

  const timelineConvergence = useMemo(
    () => highlights
      .filter((h) => h.type === 'convergence')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [highlights],
  );

  const timelineBreakouts = useMemo(() => {
    const sorted = highlights
      .filter((h) => h.type === 'breakout')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    // Group by day
    const groups = {};
    for (const h of sorted) {
      const day = h.created_at ? new Date(h.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Unknown';
      if (!groups[day]) groups[day] = [];
      groups[day].push(h);
    }
    return groups;
  }, [highlights]);

  const hasNoHighlights = !loading && !error && highlights.length === 0;

  if (!unlocked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5', gap: '12px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px' }}>the scene</h1>
        <input
          type="password"
          placeholder="Enter password"
          value={pwInput}
          onChange={e => { setPwInput(e.target.value); setPwError(false); }}
          onKeyDown={e => e.key === 'Enter' && handleUnlock()}
          style={{ padding: '10px 16px', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '8px', outline: 'none', width: '240px' }}
          autoFocus
        />
        {pwError && <span style={{ color: 'red', fontSize: '0.85rem' }}>Incorrect password</span>}
        <button
          onClick={handleUnlock}
          style={{ padding: '10px 24px', fontSize: '1rem', background: '#000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
        >
          Enter
        </button>
      </div>
    );
  }

  return (
    <div className="app-root">
      <header className="app-header">
        <div className="app-header-left">
          <h1 className="app-title">the scene</h1>
          <span className="app-subtitle">Highlights from your media universe</span>
        </div>
        <div className="app-header-right">
          <span className="pill pill-neutral">
            {newSinceLastVisit > 0
              ? `${newSinceLastVisit} new since last visit`
              : 'Up to date'}
          </span>
        </div>
      </header>

      {devMode && (
        <div style={{ borderBottom: '1px solid #e5e7eb', background: '#fff', padding: '0 24px', display: 'flex', gap: '0' }}>
          <button
            onClick={() => setActiveTab('feed')}
            style={{ padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === 'feed' ? '700' : '400', borderBottom: activeTab === 'feed' ? '2px solid #000' : '2px solid transparent', fontSize: '0.9rem' }}
          >Feed</button>
          <button
            onClick={() => setActiveTab('timeline')}
            style={{ padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === 'timeline' ? '700' : '400', borderBottom: activeTab === 'timeline' ? '2px solid #000' : '2px solid transparent', fontSize: '0.9rem' }}
          >Timeline</button>
        </div>
      )}

      {activeTab === 'feed' && (
        <main className="app-main">
        {loading && (
          <div className="state-message state-loading">Loading the latest highlights…</div>
        )}
        {error && (
          <div className="state-message state-error">
            Failed to load highlights: {error}
          </div>
        )}
        {hasNoHighlights && <div className="state-message">No highlights yet.</div>}

        <section className="grid">
          {convergenceHighlights.map((h) => (
            <ConvergenceCard
              key={h.id}
              highlight={h}
              postsById={postsById}
              devMode={devMode}
              creatorAvatars={creatorAvatars}
              isExpanded={expandedIds.has(h.id)}
              onToggleExpand={() => toggleExpanded(h.id)}
            />
          ))}
          {breakoutHighlights.map((h) => (
            <BreakoutCard key={h.id} highlight={h} postsById={postsById} devMode={devMode} creatorAvatars={creatorAvatars} />
          ))}
        </section>

        </main>
      )}
      {devMode && activeTab === 'timeline' && (
        <main style={{ padding: '24px 0', background: '#f9f9f9', minHeight: '100vh' }}>
          <TimelineView
            timelineConvergence={timelineConvergence}
            timelineBreakouts={timelineBreakouts}
            postsById={postsById}
            creatorAvatars={creatorAvatars}
            expandedIds={expandedIds}
            toggleExpanded={toggleExpanded}
          />
        </main>
      )}
    </div>
  );
}

export default App;
